import { useContext, useRef, useState } from "react";
import { AuthContext } from "../context/authContext";
import client from "../utils/client";
import newIdempotencyKey from "../utils/idempotencyKey";
import { useNavigate } from "react-router-dom";

const useDeposit = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  // A request already in flight. Checked synchronously because two clicks can
  // land in the same React batch, before `loading` has re-rendered the button
  // into its disabled state.
  const inFlight = useRef(false);

  // Held across retries so a resubmit after a failure is recognised by the
  // server as the same deposit, not a second one. Cleared only on success.
  const idempotencyKey = useRef(null);

  const deposit = async (asset, network, txnId, amount) => {
    if (inFlight.current) return;
    inFlight.current = true;

    if (!idempotencyKey.current) idempotencyKey.current = newIdempotencyKey();

    setLoading(true);
    setError(null);

    try {
      const res = await client({
        url: `/pool/deposit`,
        data: {
          asset,
          network,
          txnId,
          amount,
          idempotencyKey: idempotencyKey.current,
        },
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccess(true);
      idempotencyKey.current = null;
      navigate("/dashboard");
      return res.data;
    } catch (err) {
      console.log(err);
      setError(
        err?.response?.data?.message ??
          "Could not submit the deposit. Please try again."
      );
      setSuccess(false);
    } finally {
      // Always cleared. The old version only left the loading state on a 200,
      // so any other response left the spinner up and the form unusable.
      setLoading(false);
      inFlight.current = false;
    }
  };

  return { deposit, loading, error, success };
};

export default useDeposit;
